//      
import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";
import ReactDOM from "react-dom";
import {useTranslation} from "react-i18next";
import "./PriceInfo.scss";
import PriceInfoIcon from "./icons/PriceInfoIcon";
import currencyConverter from "../helper/currency";
                                         
import RootNodeProvider from "../helper/RootNodeProvider";

const POPUP_WIDTH = 300; // keep in sync with PriceInfo.scss
const POPUP_EDGE_PADDING = 20;

                       
               
                              
                                
                      
                   
                  
  

/**
 * Info button next to the 30-day lowest price that opens a popup with all price details (#55467).
 * The popup is rendered into the module root node with viewport-relative fixed coordinates, so the
 * surrounding host page layout cannot clip or misplace it. Mirrors the HPM PriceInfo component.
 */
const PriceInfo = (props                ) => {
	const {t} = useTranslation();
	const wrapperRef = useRef(null);
	const triggerRef = useRef(null);
	const [showInfo, setShowInfo] = useState(false);
	const [coords, setCoords] = useState({top: 0, left: 0, alignRight: true});

	const computeCoords = useCallback(() => {
		if (!triggerRef.current) return null;
		const rect = triggerRef.current.getBoundingClientRect();
		return {
			top: rect.bottom,
			left: rect.left + (rect.width / 2),
			alignRight: rect.left > POPUP_WIDTH - POPUP_EDGE_PADDING,
		};
	}, []);

	const updatePosition = useCallback(() => {
		const next = computeCoords();
		if (next) setCoords(next);
	}, [computeCoords]);

	useLayoutEffect(() => {
		if (!showInfo) return undefined;
		updatePosition();
		window.addEventListener("scroll", updatePosition, true);
		window.addEventListener("resize", updatePosition);
		return () => {
			window.removeEventListener("scroll", updatePosition, true);
			window.removeEventListener("resize", updatePosition);
		};
	}, [showInfo, updatePosition]);

	useEffect(() => {
		if (!showInfo) return undefined;
		const handleClickOutside = (event            ) => {
			const popup = document.getElementById("ucl-price-info-popup");
			if (wrapperRef.current && !wrapperRef.current.contains(event.target)
				&& popup && !popup.contains(event.target)) {
				setShowInfo(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [showInfo]);

	const toggleInfo = (e                  ) => {
		e.preventDefault();
		e.stopPropagation();
		if (!showInfo) updatePosition();
		setShowInfo(!showInfo);
	};

	const popup = (
		<div id="ucl-price-info-popup"
			className={`price-info-popup ${coords.alignRight ? "align-right" : "align-left"}`}
			role="dialog"
			aria-label={t("productDetail.priceBox.priceDetailsHeadline")}
			data-testid="priceInfoPopup"
			style={{position: "fixed", top: `${coords.top}px`, left: `${coords.left}px`}}
		>
			<div className="price-info-header">
				<h3>{t("productDetail.priceBox.priceDetailsHeadline")}</h3>
				<button type="button" className="close-button" aria-label={t("productDetail.priceBox.close")}
					onClick={e => {
						e.preventDefault();
						e.stopPropagation();
						setShowInfo(false);
					}}>
					&times;
				</button>
			</div>
			<div className="price-info-content">
				{props.price && (
					<div className="price-item">
						<div className="item-label">{t("productDetail.priceBox.priceToday")}:</div>
						<div className="item-value current-price">
							{currencyConverter(props.price)}
							<span className="vat-info"> {props.vatLabel}</span>
						</div>
					</div>
				)}
				{props.historicMinimalPrice && (
					<div className="price-item">
						<div className="item-label">{t("productDetail.priceBox.priceDetails30Days")}:</div>
						<div className="item-value historic-price">
							{currencyConverter(props.historicMinimalPrice)}
							{props.discountRate > 0 && <span className="discount-value"> -{props.discountRate}%</span>}
						</div>
					</div>
				)}
				{props.showMsrp && props.recommendedRetailPrice && (
					<div className="price-item">
						<div className="item-label">{t("productDetail.priceBox.recommendedRetailPrice")}:</div>
						<div className="item-value original-price">
							{currencyConverter(props.recommendedRetailPrice)}
						</div>
					</div>
				)}
			</div>
		</div>
	);

	const portalRoot = RootNodeProvider.getUclRootNode() || document.body;

	return (
		<span className="price-info-container" ref={wrapperRef}>
			<button type="button" ref={triggerRef} className="price-info-trigger"
				aria-label={t("productDetail.priceBox.priceDetails")} aria-expanded={showInfo}
				data-testid="priceInfoTrigger" onClick={toggleInfo}>
				<PriceInfoIcon/>
			</button>
			{showInfo && portalRoot && ReactDOM.createPortal(popup, portalRoot)}
		</span>
	);
};

export default PriceInfo;
